import React from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AlertCircle, ArrowLeft } from 'lucide-react';

// The help pages are Markdown files under src/help. Bundling them as raw
// strings keeps them editable as documents — reviewable as prose in a diff —
// while still shipping with the application, so help is available even when
// nothing else is reachable.
const pages = import.meta.glob('../help/*.md', { query: '?raw', import: 'default', eager: true }) as Record<
  string,
  string
>;

function topicSource(topic: string): string | undefined {
  return pages[`../help/${topic}.md`];
}

export function helpTopics(): string[] {
  return Object.keys(pages)
    .map(path => path.replace('../help/', '').replace('.md', ''))
    .sort();
}

export function Help() {
  const { topic = 'index' } = useParams<{ topic?: string }>();
  const source = topicSource(topic);

  if (!source) {
    return (
      <div className="empty-state">
        <AlertCircle size={48} />
        <h3>No help page called “{topic}”</h3>
        <p>It may have been renamed.</p>
        <Link to="/help" className="btn btn-primary" style={{ marginTop: 'var(--space-4)' }}>
          All help topics
        </Link>
      </div>
    );
  }

  return (
    <div className="help-page">
      {topic !== 'index' && (
        <Link to="/help" className="btn btn-ghost btn-sm" style={{ marginBottom: 'var(--space-4)' }}>
          <ArrowLeft size={14} /> All help topics
        </Link>
      )}

      <article className="markdown-body">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            // Links within the application are routed rather than reloaded;
            // anything external opens in its own tab so a reader following a
            // link out does not lose the page they were reading.
            a({ href, children, ...props }) {
              if (href?.startsWith('/')) {
                return (
                  <Link to={href} {...props}>
                    {children}
                  </Link>
                );
              }
              return (
                <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                  {children}
                </a>
              );
            },
            // Wide tables scroll on their own rather than pushing the page
            // sideways on a narrow screen.
            table({ children, ...props }) {
              return (
                <div className="table-container">
                  <table {...props}>{children}</table>
                </div>
              );
            },
          }}
        >
          {source}
        </ReactMarkdown>
      </article>
    </div>
  );
}
